import React, { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Calendar, ChevronDown, Download, Filter, Loader2, Search, X } from "lucide-react";
import { useTranslation } from "react-i18next"; // Import hook i18n
import { getPublicDocuments } from "@/services/publicDocumentService";
import { getFileTypeMeta } from "@/lib/fileType";

const Documents: React.FC = () => {
  const { t, i18n } = useTranslation(['public', 'common']); // Gunakan namespace public & common
  const [search, setSearch] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("All");

  const PAGE_SIZE = 8;
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);

  const { data: documents, isLoading } = useQuery({
    queryKey: ["public-documents"],
    queryFn: getPublicDocuments,
  });

  const categories = useMemo(() => {
    if (!documents) return ["All"];
    const unique = new Set<string>();
    documents.forEach((doc) => { if (doc.category) unique.add(doc.category); });
    return ["All", ...Array.from(unique).sort()];
  }, [documents]);

  const filteredDocs = (documents || []).filter(
    (doc) =>
      (doc.title.toLowerCase().includes(search.toLowerCase()) ||
        (doc.description || "").toLowerCase().includes(search.toLowerCase()) ||
        (doc.file_type || "").toLowerCase().includes(search.toLowerCase())) &&
      (selectedCategory === "All" || doc.category === selectedCategory)
  );

  useEffect(() => {
    setVisibleCount(PAGE_SIZE);
  }, [search, selectedCategory]);

  const visibleDocs = filteredDocs.slice(0, visibleCount);

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    // Gunakan i18n.language agar format tanggal otomatis mengikuti bahasa (id-ID atau en-US)
    return date.toLocaleDateString(i18n.language === 'id' ? 'id-ID' : 'en-US', {
      day: "2-digit",
      month: "long",
      year: "numeric",
    });
  };

  return (
    <section id="documents" className="py-20 bg-muted/30">
      <div className="container mx-auto px-4">
        {/* Header */}
        <div className="text-center mb-10">
          <h2 className="text-4xl font-bold mb-2 bg-gradient-to-r from-blue-600 to-amber-600 bg-clip-text text-transparent">
            {t('documents.title')}
          </h2>
          <p className="text-muted-foreground text-lg">
            {t('documents.description')}
          </p>
        </div>

        {/* Search Card */}
        <div className="max-w-5xl mx-auto mb-6 bg-white dark:bg-gray-900 border border-border rounded-2xl shadow-lg p-6 transition-all duration-300 hover:shadow-xl space-y-4">
          <div className="relative w-full">
            <Search
              className={`absolute left-5 top-1/2 -translate-y-1/2 w-6 h-6 transition-colors duration-300 ${search ? "text-blue-600" : "text-gray-400"}`}
            />

            <Input
              type="text"
              placeholder={t('documents.search_placeholder')}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-14 mt-3 mb-6 pr-12 h-12 text-lg rounded-xl border border-border shadow-sm focus-visible:ring-2 focus-visible:ring-blue-500 transition-all duration-300 hover:shadow-md"
            />

            {search && (
              <button
                onClick={() => setSearch("")}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-red-500 transition-colors duration-200"
              >
                <X className="w-6 h-6" />
              </button>
            )}
          </div>

          {categories.length > 2 && (
            <div className="flex flex-wrap items-center gap-2">
              <span className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-gray-400 mr-1">
                <Filter className="w-3.5 h-3.5" /> {t('gallery.filter', 'Filter:')}
              </span>
              {categories.map((cat) => (
                <button
                  key={cat}
                  onClick={() => setSelectedCategory(cat)}
                  className={`px-4 py-1.5 rounded-full text-xs font-semibold transition-all duration-300 ${
                    selectedCategory === cat
                      ? "bg-blue-600 text-white shadow shadow-blue-600/30"
                      : "bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700"
                  }`}
                >
                  {cat === "All" ? t('gallery.all_albums', 'Semua') : cat}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Document List */}
        <div className="max-w-5xl mx-auto">
          {isLoading ? (
            <div className="flex flex-col items-center justify-center py-20">
              <Loader2 className="w-10 h-10 animate-spin text-blue-600 mb-3" />
              <p className="text-gray-500 dark:text-gray-400 animate-pulse">{t('common:loading', 'Memuat...')}</p>
            </div>
          ) : filteredDocs.length > 0 ? (
            <>
              <div className="flex flex-col gap-3">
                {visibleDocs.map((doc) => {
                  const meta = getFileTypeMeta(doc.file_type);
                  const FileIcon = meta.icon;
                  return (
                    <div
                      key={doc.id}
                      className="group bg-white dark:bg-gray-900 border border-border rounded-2xl shadow-sm hover:shadow-lg transition-all duration-300 p-5 flex flex-col sm:flex-row sm:items-center gap-4"
                    >
                      <div className={`shrink-0 w-14 h-14 rounded-xl flex items-center justify-center ${meta.iconWrapClass}`}>
                        <FileIcon className="w-7 h-7" />
                      </div>

                      <div className="flex-1 min-w-0">
                        <h3 className="font-semibold text-gray-900 dark:text-gray-100 truncate">
                          {doc.title}
                        </h3>
                        {doc.description && (
                          <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5 line-clamp-2">
                            {doc.description}
                          </p>
                        )}
                        <div className="flex items-center gap-2 flex-wrap mt-2">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-[11px] font-bold uppercase ${meta.badgeClass}`}>
                            {doc.file_type || "FILE"}
                          </span>
                          {doc.category && (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-[11px] font-medium bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300">
                              {doc.category}
                            </span>
                          )}
                          <span className="inline-flex items-center gap-1 text-[11px] text-gray-400">
                            <Calendar className="w-3 h-3" />
                            {formatDate(doc.created_at)}
                          </span>
                        </div>
                      </div>

                      <Button
                        asChild
                        className="shrink-0 bg-blue-600 hover:bg-blue-700 text-white rounded-xl shadow-md shadow-blue-600/20 gap-2 self-end sm:self-center"
                      >
                        <a href={doc.file_url} target="_blank" rel="noopener noreferrer">
                          <Download className="w-4 h-4" />
                          {t('common:buttons.download', 'Unduh')}
                        </a>
                      </Button>
                    </div>
                  );
                })}
              </div>

              <div className="flex flex-col items-center mt-8 gap-4">
                <div className="text-sm text-gray-500 font-medium">
                  {t('documents.showing', { count: visibleDocs.length, total: filteredDocs.length, defaultValue: `Menampilkan ${visibleDocs.length} dari ${filteredDocs.length} dokumen` })}
                </div>
                
                <div className="w-48 sm:w-64 h-1.5 bg-gray-200 dark:bg-gray-800 rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-blue-600 transition-all duration-500 rounded-full"
                    style={{ width: `${(visibleDocs.length / filteredDocs.length) * 100}%` }}
                  />
                </div>

                {visibleCount < filteredDocs.length && (
                  <button
                    type="button"
                    onClick={() => setVisibleCount((c) => c + PAGE_SIZE)}
                    className="inline-flex items-center gap-2 px-8 py-3 rounded-full text-sm font-bold bg-white dark:bg-gray-800 text-blue-600 border-2 border-blue-100 dark:border-gray-700 hover:border-blue-300 hover:bg-blue-50 dark:hover:bg-gray-700 transition-all mt-2"
                  >
                    {t('common:buttons.load_more', 'Tampilkan Lebih Banyak')}
                    <ChevronDown className="w-4 h-4" />
                  </button>
                )}
              </div>
            </>
          ) : (
            <div className="text-center py-20 bg-white dark:bg-gray-900 rounded-3xl shadow-sm border border-dashed border-gray-200 dark:border-gray-800">
              <p className="text-muted-foreground">{t('documents.no_results')}</p>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default Documents;
